import React, { useEffect, useCallback, useState } from "react";
import { Alert, Card, Spinner } from "react-bootstrap";
import { useGridReport } from "./hooks/useGridReport";
import ReportToolbar from "./components/ReportToolbar";
import FilterPanel from "./components/FilterPanel";
import PivotGrid, { CellClickInfo } from "./components/PivotGrid";
import SkeletonGrid from "./components/SkeletonGrid";
import DetailModal from "./components/DetailModal";
import InterpretModal from "./components/InterpretModal";
import SaveReportModal from "./components/SaveReportModal";
import ReportSettingsModal from "./components/ReportSettingsModal";
import GridReportService from "../../_services/GridReportService";
import { useAccessControl } from "../../access-control/AccessControlContext";
import { CannedReport } from "./types";
import "./grid-report.css";

const GridReportPage: React.FC = () => {
  const {
    state,
    loadReportTypes,
    loadAxisChoices,
    loadFilters,
    executeReport,
    setReportType,
    setXDimension,
    setYDimension,
    setStartDate,
    setEndDate,
    setFilter,
    clearFilters,
    loadCannedReport,
  } = useGridReport();

  const { isAdmin } = useAccessControl();
  const [detailModal, setDetailModal] = useState<CellClickInfo | null>(null);
  const [cannedReports, setCannedReports] = useState<CannedReport[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showInterpretModal, setShowInterpretModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadCannedReports = useCallback(async () => {
    try {
      const reports = await GridReportService.getCannedReports();
      setCannedReports(reports);
    } catch {
      // silent — canned reports are optional
    }
  }, []);

  // Load report types and canned reports on mount
  useEffect(() => {
    loadReportTypes();
    loadCannedReports();
  }, [loadReportTypes, loadCannedReports]);

  // When report type changes, load axis choices and filters
  useEffect(() => {
    if (state.selectedReportType) {
      loadAxisChoices(state.selectedReportType);
      loadFilters(state.selectedReportType);
    }
  }, [state.selectedReportType, loadAxisChoices, loadFilters]);

  const handleGenerate = useCallback(() => {
    executeReport(0, 0);
  }, [executeReport]);

  const handlePageChange = useCallback(
    (xPage: number, yPage: number) => {
      executeReport(xPage, yPage);
    },
    [executeReport]
  );

  const handleCellClick = useCallback((info: CellClickInfo) => {
    setDetailModal(info);
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await GridReportService.exportReport({
        reportType: state.selectedReportType,
        xDimension: state.selectedXDimension,
        yDimension: state.selectedYDimension,
        startDate: state.startDate || undefined,
        endDate: state.endDate || undefined,
        filters: state.activeFilters.length > 0 ? state.activeFilters : undefined,
      });
    } catch {
      // surfaced by the axios wrapper; keep UI responsive
    } finally {
      setExporting(false);
    }
  }, [
    state.selectedReportType,
    state.selectedXDimension,
    state.selectedYDimension,
    state.startDate,
    state.endDate,
    state.activeFilters,
  ]);

  const handleCloseDetail = useCallback(() => {
    setDetailModal(null);
  }, []);

  const handleBookmark = useCallback(() => {
    setShowSaveModal(true);
  }, []);

  const handleSaveCanned = useCallback(
    async (name: string, description: string) => {
      await GridReportService.saveCannedReport({
        displayLabel: name,
        description,
        args: {
          type: state.selectedReportType,
          x: state.selectedXDimension,
          y: state.selectedYDimension,
          startDate: state.startDate,
          endDate: state.endDate,
          filters: state.activeFilters,
        },
      });
      await loadCannedReports();
    },
    [
      state.selectedReportType,
      state.selectedXDimension,
      state.selectedYDimension,
      state.startDate,
      state.endDate,
      state.activeFilters,
      loadCannedReports,
    ]
  );

  const handleLoadCanned = useCallback(
    (canned: CannedReport) => {
      loadCannedReport(canned);
    },
    [loadCannedReport]
  );

  const handleDeleteCanned = useCallback(
    async (id: number) => {
      try {
        await GridReportService.deleteCannedReport(id);
        setCannedReports((prev) => prev.filter((c) => c.id !== id));
      } catch {
        // silent
      }
    },
    []
  );

  return (
    <div className="grid-report-page">
      <div className="gr-header">
        <div className="gr-header-title">
          <span className="gr-header-icon">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
          </span>
          <h4>Grid Report</h4>
        </div>
        <p>Build cross-tab pivot views — pick a report, choose your axes, and drill into any cell.</p>
      </div>

      <ReportToolbar
        reportTypes={state.reportTypes}
        axisChoices={state.axisChoices}
        selectedReportType={state.selectedReportType}
        selectedXDimension={state.selectedXDimension}
        selectedYDimension={state.selectedYDimension}
        startDate={state.startDate}
        endDate={state.endDate}
        loading={state.loading}
        configLoading={state.configLoading}
        cannedReports={cannedReports}
        onReportTypeChange={setReportType}
        onXDimensionChange={setXDimension}
        onYDimensionChange={setYDimension}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onGenerate={handleGenerate}
        onBookmark={handleBookmark}
        onLoadCanned={handleLoadCanned}
        onDeleteCanned={handleDeleteCanned}
        showSettings={isAdmin}
        onOpenSettings={() => setShowSettings(true)}
      />

      <FilterPanel
        filterDefs={state.filterDefs}
        activeFilters={state.activeFilters}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
      />

      {state.error && (
        <Alert variant="danger" dismissible onClose={() => {}}>
          {state.error}
        </Alert>
      )}

      {/* Content area with loading overlay */}
      <div style={{ position: "relative", minHeight: 120 }}>
        {state.loading && (
          <div className="grid-report-loading-overlay">
            <div style={{ textAlign: "center" }}>
              <Spinner
                animation="border"
                style={{ color: "var(--brand-primary)", width: 30, height: 30 }}
              />
              <div style={{ marginTop: 10, fontSize: "0.88rem", color: "var(--text-body)", fontWeight: 600 }}>
                Generating report, please wait...
              </div>
            </div>
          </div>
        )}

        {state.gridData ? (
          <PivotGrid
            data={state.gridData}
            onPageChange={handlePageChange}
            onCellClick={handleCellClick}
            onInterpret={() => setShowInterpretModal(true)}
            onExport={handleExport}
            exporting={exporting}
          />
        ) : state.loading ? (
          <SkeletonGrid />
        ) : !state.error ? (
          <Card>
            <Card.Body className="grid-report-empty">
              <span className="gr-empty-icon">
                <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="9" y1="21" x2="9" y2="9" />
                  <line x1="3" y1="15" x2="21" y2="15" />
                  <line x1="15" y1="21" x2="15" y2="9" />
                </svg>
              </span>
              <h5>No Report Generated Yet</h5>
              <p>
                Select a report type, an X axis, and a Y axis, then click
                Generate to build your pivot view and drill into any cell.
              </p>
              <div className="gr-empty-steps">
                <span className="gr-empty-step"><b>1</b> Pick a report</span>
                <span className="gr-empty-step-arrow">→</span>
                <span className="gr-empty-step"><b>2</b> Choose X &amp; Y axes</span>
                <span className="gr-empty-step-arrow">→</span>
                <span className="gr-empty-step"><b>3</b> Generate</span>
              </div>
            </Card.Body>
          </Card>
        ) : null}
      </div>

      {detailModal && (
        <DetailModal
          show
          onHide={handleCloseDetail}
          reportType={state.selectedReportType}
          xDimension={state.selectedXDimension}
          yDimension={state.selectedYDimension}
          xValue={detailModal.xValue}
          yValue={detailModal.yValue}
          xLabel={detailModal.xLabel}
          yLabel={detailModal.yLabel}
          startDate={state.startDate || undefined}
          endDate={state.endDate || undefined}
          filters={state.activeFilters.length > 0 ? state.activeFilters : undefined}
          legendIndex={detailModal.legendIndex}
        />
      )}

      <InterpretModal
        show={showInterpretModal}
        onHide={() => setShowInterpretModal(false)}
        reportType={state.selectedReportType}
        xDimension={state.selectedXDimension}
        yDimension={state.selectedYDimension}
        startDate={state.startDate || undefined}
        endDate={state.endDate || undefined}
        filters={state.activeFilters.length > 0 ? state.activeFilters : undefined}
      />

      <SaveReportModal
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        onSave={handleSaveCanned}
      />

      {isAdmin && (
        <ReportSettingsModal
          show={showSettings}
          onClose={() => setShowSettings(false)}
          onSaved={loadReportTypes}
        />
      )}
    </div>
  );
};

export default GridReportPage;
